import { useCallback, useState } from 'react';
import parseISO from 'date-fns/parseISO';
import addDays from 'date-fns/addDays';
import startOfToday from 'date-fns/startOfToday';
import isSameDay from 'date-fns/isSameDay';
import format from 'date-fns/format';
import { maxDate, minDate } from './constants';

export function getDaysRange({ start, end }, useNextDay = true) {
  const startDate = useNextDay ? addDays(start, 1) : start;
  const endDate = end;
  const daysArray = [];
  for (let dt = startDate; dt < endDate; dt = addDays(dt, 1)) {
    daysArray.push(dt);
  }
  return daysArray;
}

export function getDisabledDates(availableDates, options = {}) {
  const { minDate, maxDate } = options;
  if (!availableDates?.length) return [];
  const disabledDates = [];
  const firstAvailableDate = parseISO(availableDates[0].date);
  const lastAvailableDate = parseISO(availableDates.at(-1).date);
  if (minDate && minDate < firstAvailableDate) {
    const gapDays = getDaysRange({
      start: minDate,
      end: firstAvailableDate,
    });
    disabledDates.push(minDate);
    disabledDates.push(...gapDays);
  } else if (minDate && firstAvailableDate < minDate) {
    const gapDays = getDaysRange({
      start: firstAvailableDate,
      end: minDate,
    });
    disabledDates.push(...gapDays);
  }
  for (let i = 0; i < availableDates.length - 1; i++) {
    if (
      !availableDates[i].availableForCheckin &&
      i &&
      !availableDates[i - 1].availableForCheckin
    ) {
      disabledDates.push(parseISO(availableDates[i].date));
    }
  }
  if (maxDate && maxDate > lastAvailableDate) {
    const gapDays = getDaysRange({
      start: lastAvailableDate,
      end: maxDate,
    });
    disabledDates.push(...gapDays);
    disabledDates.push(maxDate);
  }

  return disabledDates;
}

export const useAvailabilityPeriods = ({ calendar = [], queryDates }) => {
  const { startDate: priceStartDate, endDate: priceEndDate } = queryDates;

  const disabledDates = getDisabledDates(calendar, {
    minDate,
    maxDate,
  });

  const [{ startValue, endValue }, setValue] = useState(() => {
    const defaultStartDate = calendar?.length
      ? calendar.find(
          (d) =>
            d.availableForCheckin &&
            Number(minDate) <= Number(parseISO(d.date)),
        )
      : null;
    const defaultStart = defaultStartDate
      ? parseISO(defaultStartDate.date)
      : null;
    const minDays = defaultStartDate?.minimumNightStay
      ? defaultStartDate.minimumNightStay
      : 1;
    const defaultEnd = defaultStart ? addDays(defaultStart, minDays) : null;
    const start = priceStartDate ? parseISO(priceStartDate) : null;
    const end = priceEndDate ? parseISO(priceEndDate) : null;
    const isDisabled =
      disabledDates.length > 0 &&
      start &&
      end &&
      disabledDates.find((date) => {
        return Number(date) === Number(start) || Number(date) === Number(end);
      });
    if (isDisabled && defaultStart && defaultEnd) {
      return {
        startValue: defaultStart,
        endValue: defaultEnd,
      };
    }
    if (priceStartDate && priceEndDate) {
      return {
        startValue: priceStartDate,
        endValue: priceEndDate,
      };
    }
    if (defaultStart && defaultEnd) {
      return {
        startValue: defaultStart,
        endValue: defaultEnd,
      };
    }
    return {
      startValue: addDays(startOfToday(), 1),
      endValue: addDays(startOfToday(), 2),
    };
  });

  const minDaysRequirement = startValue
    ? calendar.find(
        (d) => Number(parseISO(d.date)) === Number(parseISO(startValue)),
      )
    : null;
  const minDays = minDaysRequirement?.minimumNightStay
    ? minDaysRequirement.minimumNightStay
    : 1;

  const getDaysRequirement = useCallback(
    (date) => calculateDayRequirements(calendar, date),
    [calendar],
  );

  const getMinimumDays = useCallback(
    (ranges) => calculateMinDays(calendar, ranges),
    [calendar],
  );

  const disabledDay = useCallback(
    (date) =>
      Number(date) === Number(parseISO(startValue)) ||
      Number(date) === Number(parseISO(endValue))
        ? false
        : startValue && !endValue
          ? Number(date) < Number(parseISO(startValue))
            ? !calendar.find(
                (day) =>
                  day.availableForCheckin &&
                  Number(parseISO(day.date)) === Number(date),
              )
            : !calendar.find(
                (day) =>
                  !day.availableForCheckin &&
                  Number(parseISO(day.date)) === Number(date),
              ) &&
              !calendar.find(
                (day) =>
                  day.availableForCheckin &&
                  Number(parseISO(day.date)) === Number(addDays(date, -1)),
              )
          : !calendar.find(
              (day) =>
                day.availableForCheckin &&
                Number(parseISO(day.date)) === Number(date),
            ),
    [startValue, endValue, minDays, calendar],
  );

  return {
    disabledDates,
    disabledDay,
    getMinimumDays: calendar?.length ? getMinimumDays : undefined,
    getDaysRequirement: calendar?.length ? getDaysRequirement : undefined,
    startValue,
    endValue,
    setValue,
  };
};

export function calculateMinDays(dailyMinStays, ranges) {
  if (!dailyMinStays?.length || !ranges?.length) return [];
  const isRangeSelected =
    ranges?.length > 0 && !isSameDay(ranges[0].startDate, ranges[0].endDate);
  if (isRangeSelected) return [];
  const dayRequirement = dailyMinStays.find((r) =>
    isSameDay(new Date(r.date), new Date(ranges[0].startDate)),
  );
  if (!dayRequirement) return [];
  let dt = new Date(dayRequirement.date);
  const result = [];
  // Start visually disabling days from the next day
  for (let i = 1; i < (dayRequirement?.minimumNightStay || 1); i++) {
    dt = addDays(dt, 1);
    result.push(dt);
  }
  return result;
}

export function calculateDayRequirements(dailyMinStays, date) {
  return dailyMinStays.find((r) => isSameDay(new Date(r.date), date)) || null;
}
